import React, { useEffect, useState } from "react";
import ProfileCard from "../common/profileCard";
import { User } from "../../../core/types/types";
import { useDispatch, useSelector } from "react-redux";
import apiInstance from "../../../utility/apiInstance";
import RecruiterSidebar from "../common/recruiterSidebar";
import RecruiterStatusGate from "../common/recruiterStatusGate";
import AddAccountManagerPopup from "./addAccountManagerPopup";
import { backupRecruiter, setCredentials, updateUser } from "../../../core/redux/authSlice";
import { useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";

const RecruiterAccountManagers = () => {
    const user = useSelector((state: any) => state.auth.user);
    const [accountManagers, setAccountManagers] = useState<User[]>([]);
    const [addPopupOpen, setAddPopupOpen] = useState<boolean>(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const addAccountManagerHandler = (accountManager: User) => {
        setAccountManagers([accountManager, ...accountManagers]);
    };

    useEffect(() => {
        const fetchAccountManagers = async () => {
            try {
                const res = await apiInstance.get(
                    `/api/users?where[and][0][role][equals]=account_manager&where[and][1][recruiterManager][equals]=${user.id}`
                );
                setAccountManagers(res.data.docs);
            } catch (err) {
                console.error("Failed to fetch account managers:", err);
            }
        };

        if (user?.id) fetchAccountManagers();
    }, [user?.id]);

    const switchAccountManager = async (accountManagerId: string | undefined) => {
        try {
            dispatch(backupRecruiter({ returnTo: all_routes.recruiterAccountManagers }));

            const res = await apiInstance.get(
                `/api/recruiters/switch-account-manager/${accountManagerId}`
            );
            const { token, accountManager } = res.data;

            if (token && accountManager) {
                dispatch(setCredentials({ token, userId: accountManager.id }));
                dispatch(updateUser(accountManager));
                navigate(all_routes.recruiterDashboard);
            } else {
                console.error("Switch failed: No token or account manager returned");
            }
        } catch (err) {
            console.error("Failed to switch to account manager:", err);
        }
    };

    return (
        <>
            <div className="content">
                <div className="container">
                    <ProfileCard />
                    <div className="row">
                        <RecruiterSidebar />
                        <div className="col-lg-9">
                            <RecruiterStatusGate>
                                <div className="page-title d-flex flex-wrap gap-3 align-items-center justify-content-between">
                                    <h5>Account Managers</h5>
                                    <div className="tab-list">
                                        <ul className="nav mb-0 gap-2" role="tablist">
                                            <li className="nav-item mb-0" role="presentation">
                                                <button
                                                    onClick={() => setAddPopupOpen(true)}
                                                    className="btn btn-primary font-heading icon-send-letter"
                                                >
                                                    Add Account Manager
                                                </button>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                                <div
                                    className="alert alert-info d-flex align-items-start mt-3"
                                    role="alert"
                                    style={{ borderRadius: "8px" }}
                                >
                                    <i className="isax isax-info-circle me-2"></i>
                                    <div>
                                        Account managers get full access to the recruiter dashboard and can manage their own students.
                                        Click the{" "}
                                        <i style={{ fontSize: "15px" }} className="isax isax-eye"></i>{" "}
                                        icon under <strong>Actions</strong> to switch to their account. You can switch back to your{" "}
                                        <strong>recruiter dashboard</strong> anytime.
                                    </div>
                                </div>

                                <div className="table-responsive custom-table">
                                    <table className="table">
                                        <thead className="thead-light">
                                            <tr>
                                                <th>Email</th>
                                                <th>First Name</th>
                                                <th>Last Name</th>
                                                <th>Phone</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {accountManagers && accountManagers.length > 0
                                                ? accountManagers.map((item: User) => (
                                                      <tr key={item.id}>
                                                          <td>{item?.email || "-"}</td>
                                                          <td>{item?.firstName || "-"}</td>
                                                          <td>{item?.lastName || "-"}</td>
                                                          <td>{item?.phone || "-"}</td>
                                                          <td>
                                                              <i
                                                                  className="isax isax-eye me-2 cursor-pointer"
                                                                  onClick={() => switchAccountManager(item.id)}
                                                              ></i>
                                                          </td>
                                                      </tr>
                                                  ))
                                                : <tr><td colSpan={5} className="text-center">No account managers</td></tr>}
                                        </tbody>
                                    </table>
                                </div>
                            </RecruiterStatusGate>
                        </div>
                    </div>
                </div>
                <AddAccountManagerPopup
                    show={addPopupOpen}
                    onClose={() => setAddPopupOpen(false)}
                    addAccountManagerHandler={addAccountManagerHandler}
                />
            </div>
        </>
    );
};

export default RecruiterAccountManagers;
